import { useEffect, useState } from "react";
import { Modal } from "react-bootstrap";
import Button from "react-bootstrap/Button";
import { useTranslation } from "react-i18next";
import { BiSolidError } from "react-icons/bi";
import { FaPlus } from "react-icons/fa";
import { FiEdit } from "react-icons/fi";
import { IoCloseSharp, IoSearchSharp } from "react-icons/io5";
import { RiDeleteBin6Fill } from "react-icons/ri";
import "react-loading-skeleton/dist/skeleton.css";
import ReactPaginate from "react-paginate";
import { NavLink } from "react-router-dom";
import { toast } from "react-toastify";
import { useDebounce } from "use-debounce";
import SkeletonLoading from "../../component/SkeletonLoading";
import {
  useDeleteSeatAvailabilityMutation,
  useGetSeatAvailabilityQuery,
} from "../../store/api/vendor/seatAvailabilitySlice";
import AdminPagination from "../../component/pagination/AdminPagination";

const initialActions = {
  search: "",
};

const initialPagination = {
  limit: 10,
  page: 1,
  total: "",
  totalPages: "",
};
const SeatAvailability = () => {
  const { t } = useTranslation();
  const [show, setShow] = useState(false);
  const [id, setId] = useState("");

  const [search, setSearch] = useState("");
  const [debouncedSearch] = useDebounce(search.trim(), 300);

  const [seatAvailability, setSeatAvailability] = useState([]);
  const [action, setAction] = useState(initialActions);
  const [pagination, setPagination] = useState(initialPagination);

  const { data, isSuccess, isLoading, isError, error, isFetching } =
    useGetSeatAvailabilityQuery({ ...pagination, ...action });

  // delete query
  const [deleteSeatAvailability, { isLoading: isDeleteLoading }] =
    useDeleteSeatAvailabilityMutation();

  useEffect(() => {
    if (isSuccess && data?.data) {
      setSeatAvailability(data.data.seatAvailability);
      setPagination((prev) => ({
        ...prev,
        total: data.data.pagination.total,
        page: data.data.pagination.page,
        limit: data.data.pagination.limit,
        totalPages: data.data.pagination.pages,
      }));
    }
  }, [data, isSuccess]);

  // model show function
  const handleShow = (id) => {
    setShow(true);
    setId(id);
  };

  // delete function
  const handleDelete = async () => {
    toast.dismiss();
    try {
      const response = await deleteSeatAvailability(id).unwrap();
      if (response.statusCode === 200) {
        toast.success(response.message);
        setShow(false);
        setId("");
      }
    } catch (err) {
      const mesg = err.data?.message || t("something_went_wrong");
      toast.error(mesg);
    }
  };

  // search State & Debounce Setup
  useEffect(() => {
    if (debouncedSearch) {
      setPagination((prev) => ({ limit: 10, page: 1 }));
      setAction((prev) => ({ ...prev, search: search.trim() }));
    }
  }, [debouncedSearch]);

  useEffect(() => {
    if (!search.trim()) {
      setPagination((prev) => ({ limit: 10, page: 1 }));
      setAction((prev) => ({ ...prev, search: "" }));
    }
  }, [search]);

  const handlePageChange = (page) => {
    setPagination((prev) => ({ ...prev, page }));
  };

  const handleLimitChange = (limit) => {
    setPagination((prev) => ({
      ...prev,
      limit,
      page: 1,
    }));
  };

  const handleClose = () => setShow(false);

  return (
    <>
      <div className="dbCon">
        <div className="dbRight">
          <div>
            <div className="d-flex flex-wrap align-items-center justify-content-between">
              <div className="mb-2 total">
                {t("total")} {t("seat_availability")}: {pagination?.total}
              </div>
              <div className="d-flex justify-content-end mb-2">
                <NavLink to="add" className="page-add-btn d-flex align-items-center gap-2">
                  <FaPlus /> {t("btns.add")} {t("seat_availability")}
                </NavLink>
              </div>
            </div>
            <div className="mb-2 filterBox">
              <ul className="d-flex flex-wrap align-items-center p-0 vehilcleFilter">
                <li>
                  <div className="searchBox">
                    <input
                      type="text"
                      className="input"
                      placeholder={t("search")}
                      name="search"
                      value={search}
                      onChange={(e) => setSearch(e.target.value)}
                    />{" "}
                    <Button className="searchBtn">
                      <IoSearchSharp />
                    </Button>
                  </div>
                </li>
              </ul>
            </div>
            <div className="table m-0">
              <table>
                <thead>
                  <tr>
                    <th>{t("Sr")}.{t("no")}.</th>
                    <th>{t("vehicle")}</th>
                    <th>{t("license")} {t("number")}</th>
                    <th>{t("route")}</th>
                    <th>{t("schedule")}</th>
                    <th>{t("available")} {t("seats")}</th>
                    <th>{t("total")} {t("seats")}</th>
                    <th>{t("action")}</th>
                  </tr>
                </thead>
                <tbody>
                  {isLoading || isFetching ? (
                    <SkeletonLoading length={8} />
                  ) : seatAvailability.length > 0 ? (
                    seatAvailability.map((item, i) => (
                      <tr key={i}>
                        <td>
                          {" "}
                          {(pagination.page - 1) * pagination.limit + i + 1}
                        </td>
                        <td className="text-nowrap">
                          {item?.vehicle?.vehicle_type || ""}
                        </td>
                        <td className="text-nowrap">
                          {item?.vehicle?.license_number || ""}
                        </td>
                        <td className="text-nowrap">
                          {item?.route?.route_name || ""}
                        </td>
                        <td className="text-nowrap">{item?.schedule || ""}</td>
                        <td>{item?.available_seats || ""}</td>
                        <td>{item?.total_seats || ""}</td>
                        <td>
                          <div className="actionBtn d-flex">
                            <NavLink
                              to={`/seat-availability/edit/${item.id}`}
                              className="me-2"
                            >
                              <Button title="Edit" className="rounded-circle">
                                <FiEdit />
                              </Button>
                            </NavLink>
                            <Button
                              title="Delete"
                              className="rounded-circle"
                              onClick={() => handleShow(item.id)}
                            >
                              <RiDeleteBin6Fill />
                            </Button>
                          </div>
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr className="text-center">
                      <td colSpan="12">
                        <div>
                          <span>{t("no")} {t("seat_availability")} {t("found")}</span>
                        </div>
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>

      <AdminPagination
        totalPages={pagination.totalPages}
        currentPage={pagination.page}
        limit={pagination.limit}
        onPageChange={handlePageChange}
        onLimitChange={handleLimitChange}
      />
      <Modal show={show}>
        <Button className="closePopup" onClick={handleClose}>
          <IoCloseSharp />
        </Button>
        <div className="popupBox p-3 p-md-5">
          <div>
            <div className="text-center mb-3 errorIcon">
              <BiSolidError />
            </div>
            <div className="text-center mb-4 deleteHd">
              {`${t("are")} ${t("you")} ${t("sure")} ${t("want")} ${t("to")} ${t("delete")}?`}
            </div>
            <div className="text-center">
              <Button className="smallBtn cancel me-3" onClick={handleClose}>
                {t("btns.cancel")}
              </Button>
              <Button className="smallBtn auto" onClick={handleDelete}>
                {isDeleteLoading ? (
                  <div className="spinner-border" role="status">
                    <span className="sr-only p-0"></span>
                  </div>
                ) : (
                  t("btns.delete")
                )}
              </Button>
            </div>
          </div>
        </div>
      </Modal>
    </>
  );
};

export default SeatAvailability;
